// Static fixtures dump from eligibility-prototype repo.
// Re-generate via `npm run dump-fixtures` in eligibility-prototype, then copy
// dist/fixtures.json into this folder.

import type { AppointmentResponse } from '@store/generated/appointments';

import type { VisitStatus } from '../theme/visitStatus';
import type { BlockState } from '../types/blockState';
import type { WaitlistEntry } from '../types/waitlist';

import fixturesData from './fixtures.json';

// `visit_status` is in the generated AppointmentResponse as a loose `string`;
// override the key with the strict VisitStatus union so VisitStatusEditor and
// the visit-status icon get a real enum to discriminate on.
export type ExperimentalAppointment = Omit<AppointmentResponse, 'visit_status'> & {
  block_state: BlockState;
  eligibility_snapshot_id?: string | null;
  provider_id?: string | null;
  // Fields present in fixtures but not (yet) in the OpenAPI-generated type.
  visit_type?: string | null;
  cpt?: string | null;
  visit_status?: VisitStatus;
};

export interface Provider {
  provider_id: string;
  name: string;
  title?: string | null;
  color?: string | null;
}

// Loose shape — the raw Nirvana payload has many fields the drawer reads.
// Source of truth: eligibility-prototype/research/nirvana-real-responses/*.json
export type NirvanaSnapshot = Record<string, unknown>;

interface FixturesPayload {
  _metadata: {
    generated_at: string;
    demo_now: string;
    scenario_count: number;
    snapshot_count: number;
    provider_count?: number;
    waitlist_count?: number;
    contract_version: string;
    notes?: string[];
  };
  appointments: ExperimentalAppointment[];
  providers?: Provider[];
  eligibility_snapshots: Record<string, NirvanaSnapshot>;
  waitlist_entries?: WaitlistEntry[];
}

const FIXTURES = fixturesData as unknown as FixturesPayload;

export const FIXTURES_DEMO_NOW = new Date(FIXTURES._metadata.demo_now);

export const getFixtureAppointments = (): ExperimentalAppointment[] => FIXTURES.appointments;

export const getFixtureSnapshot = (id: string | null | undefined): NirvanaSnapshot | null => {
  if (!id) return null;
  return FIXTURES.eligibility_snapshots[id] ?? null;
};

const PROVIDERS_BY_ID: Record<string, Provider> = Object.fromEntries(
  (FIXTURES.providers ?? []).map((p) => [p.provider_id, p]),
);

export const getFixtureProviders = (): Provider[] => FIXTURES.providers ?? [];

export const getFixtureProvider = (id: string | null | undefined): Provider | null => {
  if (!id) return null;
  return PROVIDERS_BY_ID[id] ?? null;
};

// ─── Waitlist fixtures (GDN-1214) ────────────────────────────────────────
//
// Mirrors the `GET /v2/provider/waitlist` and `GET .../candidates` BE routes.
// Filtering rules per the ticket:
//   list       → status === 'active' AND (expires_at == null OR expires_at > now)
//   candidates → list rules + (preferred_user_id == provider_id OR preferred == null)
//
// `_metadata.demo_now` anchors the "current" time so expired-by-date rows
// behave consistently regardless of when this code is run.

const NOW_MS = FIXTURES_DEMO_NOW.getTime();

const isLiveEntry = (entry: WaitlistEntry): boolean => {
  if (entry.status !== 'active') return false;
  if (entry.expires_at && new Date(entry.expires_at).getTime() <= NOW_MS) return false;
  return true;
};

export const getFixtureWaitlistEntries = (): WaitlistEntry[] =>
  (FIXTURES.waitlist_entries ?? []).filter(isLiveEntry);

/**
 * "Given this slot, who's waiting?" — matches the candidates query the
 * cancel-flow wires into. Pass the cancelled appointment's provider_id.
 * Entries with no `preferred_user_id` are anyone-OK and surface for every
 * provider's slot.
 */
export const getFixtureWaitlistCandidates = (
  providerId: string | null | undefined,
): WaitlistEntry[] => {
  const live = getFixtureWaitlistEntries();
  return live.filter(
    (e) => e.preferred_user_id === null || e.preferred_user_id === providerId,
  );
};
